import { Modal, notification } from "antd"
import { Download } from "lucide-react"
import { useCallback } from "react"
import { useTranslation } from "react-i18next"

import { downloadPreMeeting } from "~/api/pre-meeting"
import Loader from "~/components/base-loader"
import CustomTooltip from "~/components/custom-tooltip"
import { EVENTS } from "~/containers/knowledge-os/pre-meeting/constants"
import useGlobalStore from "~/store/global"
import { track } from "~/utils/amplitude"
import { getBaseEventProps } from "~/utils/amplitude-events"

export default function DownloadPreMeeting({
  accountName,
  meetingId,
  isDisabled,
  peopleCount
}: {
  meetingId: string
  accountName: string
  isDisabled: boolean
  peopleCount: number
}) {
  const { t } = useTranslation()
  const [notificationApi, notificationContextHolder] =
    notification.useNotification()
  const [modalApi, modalContextHolder] = Modal.useModal()
  const tokenInfo = useGlobalStore((store) => store.tokenInfo)

  const onClickDownload = useCallback(async () => {
    const instance = modalApi.confirm({
      title: t("pre_meeting_brief.modal_downloading_pre_meeting_brief"),
      content: (
        <div className="flex items-center justify-center mt-6">
          <Loader />
        </div>
      ),
      okText: t("label_ok"),
      cancelText: t("label_cancel"),
      footer: null,
      centered: true,
      classNames: {
        content: "dark:!bg-dark-2"
      }
    })

    try {
      track(EVENTS.DOWNLOAD_CLICKED, {
        ...getBaseEventProps({ feature: "pre_meeting_brief", accountName }),
        timestamp: Date.now(),
        account_name: accountName
      })
      const response = await downloadPreMeeting(tokenInfo!, meetingId!)
      const blobUrl = window.URL.createObjectURL(response.data)
      const link = document.createElement("a")
      link.href = blobUrl
      link.download = `Pre-meeting brief ${accountName}.pdf`
      document.body.appendChild(link)
      link.click()
      setTimeout(() => {
        window.URL.revokeObjectURL(blobUrl) // Free up memory
        document.body.removeChild(link)
      }, 0)
      instance.destroy()
      notificationApi.success({
        message: t("pre_meeting_brief.notif_download_success")
      })
      track(EVENTS.PRE_MEETING_BRIEF_DOWNLOAD_CLICKED, {
        ...getBaseEventProps({ feature: "pre_meeting_brief", accountName }),
        account_name: accountName,
        people_included: peopleCount,
        download_status: "success"
      })
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error) {
      instance.destroy()
      notificationApi.error({
        message: t("pre_meeting_brief.error_generic"),
        description: t("pre_meeting_brief.notif_download_failed")
      })
      track(EVENTS.PRE_MEETING_BRIEF_DOWNLOAD_CLICKED, {
        ...getBaseEventProps({ feature: "pre_meeting_brief", accountName }),
        account_name: accountName,
        download_status: "failed",
        people_included: peopleCount,
        error_message: error
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokenInfo, accountName, t])

  return (
    <>
      {modalContextHolder}
      {notificationContextHolder}
      <CustomTooltip
        title={
          <span className="text-xs">
            {t("pre_meeting_brief.tooltip_download_brief")}
          </span>
        }
      >
        <button
          onClick={onClickDownload}
          className="flex items-center gap-1 text-xs font-medium text-primary dark:text-primary-light"
          disabled={isDisabled}
        >
          <Download size={14} />
          {t("pre_meeting_brief.cta_download_pre_meeting_brief")}
        </button>
      </CustomTooltip>
    </>
  )
}
